import React, { useEffect, useRef } from 'react';
import imageCompression from 'browser-image-compression';

const CdnEditor = ({ value, onChange, toolbar }) => {
    const editorRef = useRef(null);
    const editorInstanceRef = useRef(null);

    // Set default toolbar if none is provided
    const defaultToolbar = [
        'undo', 'redo', '|',
        'heading', '|',
        'bold', 'italic', 'link', '|',
        'bulletedList', 'numberedList', '|',
        'imageUpload', // default includes image
        'blockQuote'
    ];

    useEffect(() => {
        const editorElement = editorRef.current;
        if (!window.ClassicEditor || !editorElement || editorInstanceRef.current) return;

        const editorConfig = {
            extraPlugins: [CustomUploadAdapterPlugin],
            toolbar: toolbar || defaultToolbar
        };

        // Only add image config if image plugin is in use
        if ((toolbar || defaultToolbar).includes('imageUpload')) {
            editorConfig.image = {
                toolbar: [
                    'imageStyle:inline',
                    'imageStyle:block',
                    'imageStyle:side',
                    '|',
                    'toggleImageCaption',
                    'imageTextAlternative'
                ]
            };
        }

        window.ClassicEditor.create(editorElement, editorConfig)
            .then((editor) => {
                editorInstanceRef.current = editor;
                editor.setData(value || '');

                editor.model.document.on('change:data', () => {
                    const data = editor.getData();
                    onChange(data);
                });
            })
            .catch((err) => {
                console.error('CKEditor init failed:', err);
            });

        return () => {
            if (editorInstanceRef.current) {
                editorInstanceRef.current.destroy();
                editorInstanceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (editorInstanceRef.current && editorInstanceRef.current.getData() !== value) {
            editorInstanceRef.current.setData(value || '');
        }
    }, [value]);

    return (
        <div
            ref={editorRef}
            style={{
                minHeight: '300px',
                width: '100%',
                padding: '8px',
                fontSize: '16px',
                boxSizing: 'border-box',
                border: '1px solid #ccc',
                borderRadius: '4px',
            }}
        />
    );
};

export default CdnEditor;

// Plugin and Adapter stay same
function CustomUploadAdapterPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
        return new MyUploadAdapter(loader);
    };
}

class MyUploadAdapter {
    constructor(loader) {
        this.loader = loader;
    }

    async upload() {
        const file = await this.loader.file;
        console.log("NotCompressed Size (mb):", (file.size / 1024) / 1024);

        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1024,
            useWebWorker: true
        };

        let compressedFile;
        try {
            compressedFile = await imageCompression(file, options);
            console.log("Compressed Size (mb):", (compressedFile.size / 1024) / 1024);
        } catch (err) {
            console.error("Image compression failed", err);
            throw err;
        }

        const base64 = await this.convertToBase64(compressedFile);
        return { default: base64 };
    }

    convertToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (reader.result) {
                    resolve(reader.result);
                } else {
                    reject(new Error("Base64 conversion returned null or empty."));
                }
            };
            reader.onerror = (error) => {
                console.error("FileReader error:", error);
                reject(error);
            };
            reader.readAsDataURL(file);
        });
    }
}
